import { createSlice } from "@reduxjs/toolkit";
import type { Dictionary } from "lodash";
import keyBy from "lodash/keyBy";
import axios, { getData } from "../../../axios";
import {
  createErrorReportingAsyncThunk,
  serverRejectionMessage,
  ServerRejection,
} from "../../helpers";
import { CreateSiteJob, SiteJob, decodeSiteJob, decodeSiteJobs } from "./types";

const prefix = "data/site-jobs";

type State = {
  entities: Dictionary<SiteJob>;
  ids: string[];
  loading: boolean;
};

const initialState: State = { entities: {}, ids: [], loading: false };

/**
 * Site jobs whose date falls in the range shown on the dispatch page. This is
 * only used to decorate the undispatched orders with their group; the orders
 * themselves come from the normal cargo payload and are not touched here.
 */
export const loadSiteJobs = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async ({
    dateFrom,
    dateTo,
  }: {
    dateFrom: string;
    dateTo: string;
  }): Promise<SiteJob[]> =>
    await axios
      .get("/api/site-jobs", { params: { dateFrom, dateTo } })
      .then(getData)
      .then(decodeSiteJobs)
);

/**
 * Creates one order per container. The server validates the job (customer,
 * count, whether the named units may be emptied, whether END_RENTAL has the
 * destination it needs) and refuses with a named reason; that refusal becomes
 * the rejection payload, so the dialog can show it inline in the user's own
 * language. Anything else goes through the normal reporting/toast path.
 */
export const createSiteJob = createErrorReportingAsyncThunk<
  SiteJob,
  CreateSiteJob,
  { rejectValue: ServerRejection }
>(`${prefix}/create`, async (input, thunkApi) => {
  try {
    return await axios
      .post("/api/site-jobs", input)
      .then(getData)
      .then(decodeSiteJob);
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (rejection) {
      return thunkApi.rejectWithValue(rejection);
    }
    throw err;
  }
});

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadSiteJobs.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadSiteJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = keyBy(action.payload, "id");
        state.ids = action.payload.map((x) => x.id);
      })
      .addCase(loadSiteJobs.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createSiteJob.fulfilled, (state, action) => {
        const siteJob = action.payload;
        if (!siteJob) return;
        if (!state.entities[siteJob.id]) {
          state.ids.unshift(siteJob.id);
        }
        state.entities[siteJob.id] = siteJob;
      });
  },
});

export default slice.reducer;
